import { ChangeDetectorRef, ComponentFactory, ComponentFactoryResolver, ComponentRef, Directive, EventEmitter, Inject, Input, isDevMode, OnChanges, SimpleChanges, TemplateRef, Type, ViewContainerRef } from '@angular/core';
import { Data } from '@angular/router';
import { LazyloadService } from './lazyload.service';
import { LoadModuleReferences } from './models/load-module-reference.model';
import { LAZY_MODULE_COMPONENT_REFERENCE } from './tokens';
import { Guard } from '../misselaineous/guards';

class LazyError extends Error {}

@Directive({
    selector: '[lazyLoad]'
})
export class LazyLoadDirective implements OnChanges {
    module: any;
    componentRef: ComponentRef<any>;
    loaded: EventEmitter<void> = new EventEmitter<void>();
    component: any;
    extras: (() => void)[] = [];

    @Input() lazyLoad: string;
    @Input() lazyLoadIsPage: boolean;
    @Input() lazyLoadData: Data;
    @Input() lazyLoadLoading: TemplateRef<any>;
    @Input() lazyLoadError: TemplateRef<any>;
    @Input() lazyLoadOnLoad: (_: any, __: any) => void = (_, __) => {};
    @Input() lazyLoadOnError: () => void = () => {};

    constructor(
        @Inject(LAZY_MODULE_COMPONENT_REFERENCE) private lazyModuleComponentReferences: LoadModuleReferences[],
        private viewContainerRef: ViewContainerRef,
        private _template: TemplateRef<any>,
        private changeDetector: ChangeDetectorRef,
        private lazyLoadService: LazyloadService
    ) {}

    async ngOnChanges(changes: SimpleChanges): Promise<void> {
        if (changes.lazyLoad &&
            (changes.lazyLoad.previousValue !== changes.lazyLoad.currentValue) ||
            (changes.lazyLoadData && changes.lazyLoadData.previousValue !== changes.lazyLoadData.currentValue)
        ) {
            // if (!!document['createDocumentTransition'] && this.lazyLoadIsPage) {
            //     const transition = document['createDocumentTransition']();
            //     await transition.start(() => this.reload());
            // } else {
                this.reload();
            // }
        }
    }

    add(component: Type<any>, data: Data) {
        this.extras.push(() => {
            const componentRef = this.viewContainerRef.createComponent(component);
            Object.assign(componentRef.instance, data);
        });
    }

    private reload() {
        try {
            this.loading();
            this.lazyModuleComponentReferences.forEach(ref => Object.assign(this.lazyLoadService.lazyModuleReferences, ref));
            const module = this.lazyLoadService.lazyModuleReferences[this.lazyLoad]();

            this.extras = [];
            if (Guard.is.nullOrUndefined(module)) {
                if (isDevMode()) {
                    throw new LazyError(`Module not found in references ${this.lazyLoad} in ${Object.keys(this.lazyLoadService.lazyModuleReferences)}`);
                }
                throw new LazyError(`Module not found in references ${this.lazyLoad}`);
            }

            module
                .then(lazyModule => this.module = lazyModule)
                .then(() => Guard.against.nullOrUndefined(this.module.components))
                .then(() => Object.assign(this.lazyLoadService.lazyModuleReferences, this.module.references))
                .then(() => {
                    this.component = this.module.components[this.lazyLoad];
                    if (this.component == null) {
                        throw new LazyError(`Component not in module: "${this.lazyLoad}"`);
                    }
                })
                .then(() => this.viewContainerRef.clear())
                .then(() => this.componentRef = this.viewContainerRef.createComponent(this.component))
                .then(() => Object.assign(this.componentRef.instance, this.lazyLoadData))
                .then(() => this.lazyLoadOnLoad(this.lazyLoadData, this.componentRef.instance))
                .then(() => this.extras.forEach(_ => _()))
                .then(() => this.loaded.emit())
                .then(() => this.changeDetector.markForCheck())
                .catch(e => this.error(e));
        } catch (error) {
            this.error(error);
        }
    }

    private loading() {
        if (Guard.not.nullOrUndefined(this.lazyLoadLoading)) {
            this.viewContainerRef.clear();
            this.viewContainerRef.createEmbeddedView(this.lazyLoadLoading);
            this.changeDetector.markForCheck();
        }
    }

    private error(e) {
        console.error(e);
        this.lazyLoadOnError();
        if (Guard.not.nullOrUndefined(this.lazyLoadError)) {
            this.viewContainerRef.clear();
            this.viewContainerRef.createEmbeddedView(this.lazyLoadError);
            this.changeDetector.markForCheck();
        }
    }

}
